<script setup lang="ts">
import { computed, ref } from "vue";
import { PopoverClose } from "reka-ui";
import { useRouter } from "vue-router";

import { useMessageStore, useRetentionStore } from "@/stores";

import { isApiError } from "@/services/Api";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import RetentionActionPopover from "./RetentionActionPopover.vue";
import RetentionActionConfirmCase from "./RetentionActionConfirmCase.vue";

const router = useRouter();
const caseRouteParams = useCaseRouteParams();

const reason = ref("");
const caseRefInput = ref("");

const state = ref<{ status: "idle" } | { status: "loading" } | { status: "error"; message: string }>({
  status: "idle",
});

const isSubmitDisabled = computed(
  () => state.value.status === "loading" || caseRefInput.value !== caseRouteParams.value.casename,
);

async function handleSubmit() {
  if (isSubmitDisabled.value) return;

  state.value = { status: "loading" };

  try {
    await useRetentionStore().requestDeletion({ reason: reason.value });

    state.value = { status: "idle" };

    router.push({ name: "brand", params: { brand: caseRouteParams.value.brand } });

    useMessageStore().updateMessage({
      type: "success",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message: "Deletion request submitted. Awaiting approval.",
    });
  } catch (error) {
    const message =
      isApiError<{ reason?: string }>(error) && error.status === 406 && error.data?.reason
        ? error.data.reason
        : "Failed to request deletion. Please try again.";

    state.value = { status: "error", message };

    useMessageStore().updateMessage({
      type: "error",
      autoHide: true,
      showReload: false,
      title: "Case Retention",
      message,
    });
  }
}
</script>
<template>
  <RetentionActionPopover>
    <template #trigger>
      <button type="button" class="tw-btn-md-gray tw-b-r">Request Deletion</button>
    </template>
    <form class="tw-flex tw-flex-col tw-gap-2" @submit.prevent="handleSubmit">
      <p class="tw-max-w-md tw-text-balance tw-text-sm">You are about to request for this case to be deleted.</p>
      <hr />
      <RetentionActionConfirmCase v-model:case-ref="caseRefInput" v-model:reason="reason" />
      <p v-if="state.status === 'error'" role="alert" class="tw-text-sm tw-text-red-600">
        {{ state.message }}
      </p>
      <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
        <PopoverClose class="tw-btn-md-white tw-b-r">Cancel</PopoverClose>
        <button
          type="submit"
          class="tw-btn-md-gray tw-b-r tw-flex tw-items-center tw-gap-1"
          :disabled="isSubmitDisabled"
        >
          <i v-if="state.status === 'loading'" aria-hidden="true" class="fas fa-spin fa-circle-notch" />
          Request Deletion
        </button>
      </div>
    </form>
  </RetentionActionPopover>
</template>
